import React from 'react';
import { motion } from 'framer-motion';
import { FileText, CheckCircle, AlertCircle, Scale, UserX, Shield } from 'lucide-react';
export function TermsOfServicePage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1517" data-mp="1htqk3h47c10n">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1518" data-mp="1hquinit5au7g">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1519" data-mp="1cs9mbuh0kuoa">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1520" data-mp="ywfllyqe9bm9">
            <span className="w-8 h-px bg-teal" data-id="element-1521" data-mp="u2l2oxim50o8"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1522" data-mp="6e1wcnj4ixys">
              Legal
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1523" data-mp="1fqmicstwngk"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1524" data-mp="g6n6dy0jxdki">
            Terms of Service
          </h1>

          <p className="text-lg text-ink-muted" data-id="element-1525" data-mp="k6pasexo8l3h">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1526" data-mp="3tf9s11u9jze6">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1527" data-mp="1a5w7a43m4zj">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1528" data-mp="1hn4jwda6z23h">
            These Terms of Service ("Terms") govern your access to and use of
            SyNeural's systems, services, and website (collectively, the
            "Services"). By accessing or using our Services, you agree to be
            bound by these Terms.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1529" data-mp="16whmhza870o6">
            If you do not agree to these Terms, you may not access or use our
            Services. If you are using our Services on behalf of an
            organization, you represent that you have the authority to bind that
            organization to these Terms.
          </p>
        </motion.div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-16" data-id="element-1530" data-mp="ny5dcdjbdqsl">
        {/* Acceptance of Terms */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1531" data-mp="j6eybu9fmeuu">
          <div className="flex items-center gap-4 mb-6" data-id="element-1532" data-mp="224a7agx2vs9j">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1533" data-mp="26x3bn3omwyl3">
              <CheckCircle size={24} data-id="element-1534" data-mp="1s872126xjdid" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1535" data-mp="1s5m1qhma55mq">
              Acceptance of Terms
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed" data-id="element-1536" data-mp="2kvuic5163hz7">
            By creating an account, accessing our systems, or using any of our
            Services, you acknowledge that you have read, understood, and agree
            to be bound by these Terms, as well as our Privacy Policy. These
            Terms constitute a legally binding agreement between you and
            SyNeural.
          </p>
        </motion.div>

        {/* Eligibility */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1537" data-mp="10ilva81sespd">
          <div className="flex items-center gap-4 mb-6" data-id="element-1538" data-mp="1fwpktaxsxmyq">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1539" data-mp="1b07iehpy20pk">
              <UserX size={24} data-id="element-1540" data-mp="161nsy7c566sg" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1541" data-mp="23o3g9pr91ynx">
              Eligibility
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1542" data-mp="1yq1ugx2fvl8r">
            To use our Services, you must:
          </p>
          <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1543" data-mp="1tocgb8jx6rgg">
            <li data-id="element-1544" data-mp="1pnahgstx0hf0">
              Be at least 13 years of age (or the age of majority in your
              jurisdiction)
            </li>
            <li data-id="element-1545" data-mp="i8ntqiakvug6">Have the legal capacity to enter into a binding agreement</li>
            <li data-id="element-1546" data-mp="e7lsjvx3p8bk">
              Not be prohibited from using our Services under applicable law
            </li>
            <li data-id="element-1547" data-mp="2pyd72dihd1l9">Provide accurate and complete registration information</li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1548" data-mp="2tynabiew1dwf">
            For users under 18, access through educational institutions or with
            parental consent may be required.
          </p>
        </motion.div>

        {/* Account Registration and Security */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1549" data-mp="38nt76zq96s15">
          <div className="flex items-center gap-4 mb-6" data-id="element-1550" data-mp="3mlq3rw4piyft">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1551" data-mp="1n9ov2axj8tth">
              <Shield size={24} data-id="element-1552" data-mp="1s8iai8wdquwa" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1553" data-mp="1s90clm5q43c4">
              Account Registration and Security
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1554" data-mp="2ql481m8s224j">
            <p data-id="element-1555" data-mp="2qmxfh3r7sqdl">When you create an account, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1556" data-mp="34l5dzg3wfzqv">
              <li data-id="element-1557" data-mp="2zkrckgwszo1t">Provide accurate, current, and complete information</li>
              <li data-id="element-1558" data-mp="3eijrp7yfuafh">Maintain and update your information to keep it accurate</li>
              <li data-id="element-1559" data-mp="3egfdnx6trv1h">
                Maintain the security and confidentiality of your account
                credentials
              </li>
              <li data-id="element-1560" data-mp="3nbd9bc1cxlwi">
                Notify us immediately of any unauthorized access or security
                breach
              </li>
              <li data-id="element-1561" data-mp="3j9s1i53ojxt6">
                Accept responsibility for all activities that occur under your
                account
              </li>
            </ul>
            <p data-id="element-1562" data-mp="3sagfizt2py62">
              You may not share your account credentials, create multiple
              accounts, or allow others to use your account. SyNeural reserves
              the right to suspend or terminate accounts that violate these
              requirements.
            </p>
          </div>
        </motion.div>

        {/* Use of Services */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1563" data-mp="5rwi1hik8es3">
          <div className="flex items-center gap-4 mb-6" data-id="element-1564" data-mp="1se0mvpy6lu0">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1565" data-mp="3j84334faai2f">
              <FileText size={24} data-id="element-1566" data-mp="3o6nn2zabhjs5" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1567" data-mp="3a6mo5pdm4mfb">
              Permitted Use
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1568" data-mp="3e6oe519zjrer">
            You may use our Services for lawful purposes in accordance with
            these Terms. You agree not to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1569" data-mp="3ppfk81fneznr">
            <li data-id="element-1570" data-mp="3korqv93y66qd">
              Violate any applicable laws, regulations, or third-party rights
            </li>
            <li data-id="element-1571" data-mp="3fp6ik82w0m0z">
              Use the Services for any fraudulent, harmful, or malicious purpose
            </li>
            <li data-id="element-1572" data-mp="3bpei6inndm6p">
              Attempt to gain unauthorized access to our systems or other users'
              accounts
            </li>
            <li data-id="element-1573" data-mp="36w1ty9mvrheh">
              Interfere with or disrupt the integrity or performance of the
              Services
            </li>
            <li data-id="element-1574" data-mp="2x0i0ruzpawtf">
              Reverse engineer, decompile, or attempt to extract source code
            </li>
            <li data-id="element-1575" data-mp="2rzkjdkmim6sz">
              Use automated tools (bots, scrapers) without our written
              permission
            </li>
            <li data-id="element-1576" data-mp="2nztx9u7xoeus">Misrepresent your identity or affiliation</li>
            <li data-id="element-1577" data-mp="13hrggzkxqbq1">
              Share, sell, or redistribute assessment content or results without
              authorization
            </li>
            <li data-id="element-1578" data-mp="17ko5b812j42f">Use the Services to harass, abuse, or harm others</li>
          </ul>
        </motion.div>

        {/* Intellectual Property */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1579" data-mp="1hhanfp20fh0r">
          <div className="flex items-center gap-4 mb-6" data-id="element-1580" data-mp="tqwrvok7v01f">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1581" data-mp="tpdb7wunemz4">
              <Scale size={24} data-id="element-1582" data-mp="12iv1c4v69rg" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1583" data-mp="11pwgyg26qke">
              Intellectual Property Rights
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1584" data-mp="39huxfpazmh9">
            <p data-id="element-1585" data-mp="3983rosiv99z">
              All content, features, functionality, and materials available
              through our Services, including but not limited to text, graphics,
              logos, assessment frameworks, algorithms, software, and data
              compilations, are owned by SyNeural or its licensors and are
              protected by copyright, trademark, patent, and other intellectual
              property laws.
            </p>
            <p data-id="element-1586" data-mp="3lmxr4j8rctjc">
              We grant you a limited, non-exclusive, non-transferable, revocable
              license to access and use our Services for your personal or
              internal organizational use. This license does not include any
              right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1587" data-mp="2xy4rocsxg3jz">
              <li data-id="element-1588" data-mp="2xvsd89mdobbo">
                Reproduce, distribute, or create derivative works from our
                content
              </li>
              <li data-id="element-1589" data-mp="1k1legukgbbmc">
                Use our trademarks, logos, or branding without written
                permission
              </li>
              <li data-id="element-1590" data-mp="1g1kw4yu8ega0">
                Remove or alter any copyright, trademark, or proprietary notices
              </li>
              <li data-id="element-1591" data-mp="1azc3k3l9tg8f">Commercially exploit our Services or content</li>
            </ul>
          </div>
        </motion.div>

        {/* User Content and Data */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1592" data-mp="11390mvilhdkf">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1593" data-mp="ncnemxxugnib">
            User Content and Data
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1594" data-mp="ibpxmooklzie">
            <p data-id="element-1595" data-mp="deg34umbqxnp">
              You retain ownership of any content, data, or information you
              provide through our Services ("User Content"). By providing User
              Content, you grant SyNeural a worldwide, non-exclusive,
              royalty-free license to use, process, analyze, and store such
              content to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1596" data-mp="3uw3tzrl3qgsk">
              <li data-id="element-1597" data-mp="3uwdljjlsow0d">Provide and improve our Services</li>
              <li data-id="element-1598" data-mp="mfsa7w5oxqu3">Generate assessment results and recommendations</li>
              <li data-id="element-1599" data-mp="rfdiix6r3bjh">
                Conduct research and platform development (using anonymized
                data)
              </li>
              <li data-id="element-1600" data-mp="dorw4yi38jgu">Comply with legal obligations</li>
            </ul>
            <p data-id="element-1601" data-mp="hpk6jv2p4uep">
              You represent and warrant that you have all necessary rights to
              provide User Content and that such content does not violate any
              third-party rights or applicable laws.
            </p>
          </div>
        </motion.div>

        {/* Payment and Subscriptions */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1602" data-mp="2uvrv8bvngs1v">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1603" data-mp="2zmqixwiywsf5">
            Payment and Subscriptions
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1604" data-mp="2bxo1aooc2zkn">
            <p data-id="element-1605" data-mp="26ze5qcyp5wyi">
              Certain features of our Services may require payment. If you
              purchase a subscription or paid service:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1606" data-mp="2gwijboeh8k3p">
              <li data-id="element-1607" data-mp="2gv7ctgtncy8w">
                You agree to pay all applicable fees as described at the time of
                purchase
              </li>
              <li data-id="element-1608" data-mp="3sbxmj9xa4879">
                Fees are non-refundable except as required by law or as
                specified in our refund policy
              </li>
              <li data-id="element-1609" data-mp="21wemeatduwbt">
                Subscriptions automatically renew unless canceled before the
                renewal date
              </li>
              <li data-id="element-1610" data-mp="2auqn55nyrahz">
                We reserve the right to change pricing with advance notice
              </li>
              <li data-id="element-1611" data-mp="2fulch4s3bs0x">
                You are responsible for all taxes associated with your purchase
              </li>
            </ul>
            <p data-id="element-1612" data-mp="2kt62kbdmovbn">
              Failure to pay fees may result in suspension or termination of
              your access to paid Services.
            </p>
          </div>
        </motion.div>

        {/* Disclaimers and Limitations */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1613" data-mp="2yktaj8gxnsa9">
          <div className="flex items-center gap-4 mb-6" data-id="element-1614" data-mp="3imnx39qjajt6">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1615" data-mp="3io8rmzcum2r1">
              <AlertCircle size={24} data-id="element-1616" data-mp="3dpxfuj2zti66" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1617" data-mp="38nzrjafqjx6d">
              Disclaimers and Limitations of Liability
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1618" data-mp="581avon5qlmi">
            <p className="font-semibold" data-id="element-1619" data-mp="579dq9dwwfu4">
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
            </p>
            <p data-id="element-1620" data-mp="3rlssw8apnz1n">
              SyNeural disclaims all warranties, including but not limited to
              warranties of merchantability, fitness for a particular purpose,
              non-infringement, and accuracy. We do not warrant that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1621" data-mp="j7k65guve2he">
              <li data-id="element-1622" data-mp="j5zebyzxwxn8">The Services will be uninterrupted, secure, or error-free</li>
              <li data-id="element-1623" data-mp="2dhr09l3jvd4q">
                Assessment results will be accurate, complete, or suitable for
                any particular purpose
              </li>
              <li data-id="element-1624" data-mp="23qmkij61vo8u">Any defects or errors will be corrected</li>
              <li data-id="element-1625" data-mp="28oxvix8304sn">The Services will meet your specific requirements</li>
            </ul>
            <p className="font-semibold mt-6" data-id="element-1626" data-mp="28muy3s4ln9dw">LIMITATION OF LIABILITY:</p>
            <p data-id="element-1627" data-mp="1unlgqp4nq98c">
              To the maximum extent permitted by law, SyNeural shall not be
              liable for any indirect, incidental, special, consequential, or
              punitive damages, including but not limited to loss of profits,
              data, or opportunities, arising out of or related to your use of
              the Services, even if we have been advised of the possibility of
              such damages.
            </p>
            <p data-id="element-1628" data-mp="3k3uky886gw0j">
              Our total liability for any claims related to the Services shall
              not exceed the amount you paid us in the twelve months preceding
              the claim.
            </p>
          </div>
        </motion.div>

        {/* Indemnification */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1629" data-mp="13ftpkzdh5zem">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1630" data-mp="1n5xcffg3dbve">
            Indemnification
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1631" data-mp="215onb7bbac56">
            You agree to indemnify, defend, and hold harmless SyNeural, its
            affiliates, officers, directors, employees, and agents from any
            claims, liabilities, damages, losses, costs, or expenses (including
            reasonable attorneys' fees) arising out of or related to: (a) your
            use of the Services; (b) your violation of these Terms; (c) your
            violation of any third-party rights; or (d) your User Content.
          </p>
        </motion.div>

        {/* Termination */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1632" data-mp="3lum3269fzeir">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1633" data-mp="2y3yjuou2t2gw">
            Termination
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1634" data-mp="332tgatkyupjh">
            <p data-id="element-1635" data-mp="383h1uzqfee6b">
              You may terminate your account at any time by contacting us or
              using account settings. We may suspend or terminate your access to
              the Services at any time, with or without cause or notice,
              including if:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1636" data-mp="27uhovrdwyvze">
              <li data-id="element-1637" data-mp="27wu3bukgqo7p">You violate these Terms</li>
              <li data-id="element-1638" data-mp="22v4sog15kck5">Your use poses a security or legal risk</li>
              <li data-id="element-1639" data-mp="22y0o9xpj2p5f">You fail to pay applicable fees</li>
              <li data-id="element-1640" data-mp="1y3whbeg9ih4c">We discontinue the Services</li>
            </ul>
            <p data-id="element-1641" data-mp="1t64omi7smqz3">
              Upon termination, your right to use the Services will immediately
              cease. Provisions that by their nature should survive termination
              (including intellectual property rights, disclaimers, and
              limitations of liability) will remain in effect.
            </p>
          </div>
        </motion.div>

        {/* Governing Law and Dispute Resolution */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1642" data-mp="1a7ir1dbhjhzx">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1643" data-mp="8ngntguhih2">
            Governing Law and Dispute Resolution
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1644" data-mp="3ri2yrnkf59gh">
            These Terms shall be governed by and construed in accordance with
            the laws of [Jurisdiction], without regard to its conflict of law
            provisions. Any disputes arising out of or related to these Terms or
            the Services shall be resolved through binding arbitration in
            accordance with [Arbitration Rules], except that either party may
            seek injunctive relief in court. You waive any right to participate
            in class actions or class arbitrations.
          </p>
        </motion.div>

        {/* Changes to Terms */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1645" data-mp="11zap6z37c860">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1646" data-mp="19hicztx2g1z3">
            Changes to These Terms
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1647" data-mp="1ea1zjgdqe7g1">
            We reserve the right to modify these Terms at any time. We will
            notify you of material changes by posting the updated Terms on our
            website and updating the "Last Updated" date. Your continued use of
            the Services after such changes constitutes acceptance of the
            updated Terms. If you do not agree to the modified Terms, you must
            stop using the Services.
          </p>
        </motion.div>

        {/* Contact Information */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-surface-muted p-8 rounded-lg border border-line" data-id="element-1648" data-mp="26yh4tb729be6">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1649" data-mp="1w6sxwx5q8vj9">
            Contact Us
          </h2>
          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1650" data-mp="1r7q2m4fju3oh">
            If you have questions about these Terms of Service, please contact
            us:
          </p>
          <div className="space-y-2 text-ink-muted" data-id="element-1651" data-mp="yjc47nkjt5ce">
            <p data-id="element-1652" data-mp="ykn43cavhsy8">
              <strong data-id="element-1653" data-mp="yl5a30nc7ljc">Email:</strong>{' '}
              <a href="mailto:legal@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1654" data-mp="1d7ykbri0214o">
                legal@syneural.com
              </a>
            </p>
            <p data-id="element-1655" data-mp="kr487y139ier">
              <strong data-id="element-1656" data-mp="kqmg9dlglqcn">General Inquiries:</strong>{' '}
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1657" data-mp="fsugjxekozxw">
                contact@syneural.com
              </a>
            </p>
          </div>
        </motion.div>
      </section>
    </div>;
}